/**
 * EVENTSFLO-TAX-CLASS-DEFAULTS-AND-GENERATION-PREFLIGHT-V1
 *
 * Read-only Tax Policy Review section rendered inside the existing
 * "Generate Quote" dialog on ProjectDetailPage. Fetches the read-only
 * preflight endpoint and surfaces:
 *   • client tax profile summary
 *   • detected tax-policy sentence
 *   • per-tax-class line count + subtotal
 *   • estimated reverse-charge / taxable subtotals + VAT + total
 *   • manual-review / missing-class / unmatched-rule warnings
 *   • admin toggles (display-only in this iteration — actual matrix
 *     evaluation is always applied by the backend for safety)
 */
import { useEffect, useState } from "react";
import { getQuoteTaxPreflight, extractErrorMessage } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { HelpPopover } from "../shared/HelpPopover";
import { AlertTriangle, ShieldCheck, Loader2, RefreshCcw } from "lucide-react";

const CURRENCY = "€";
const fmt = (n) =>
  n == null
    ? "—"
    : `${CURRENCY}${Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

/**
 * @param {{
 *   projectId: string,
 *   open: boolean,
 *   overrides: { applyMatrix: boolean, includeRcNote: boolean, blockOnManualReview: boolean, overrideReason: string },
 *   onOverridesChange: (patch: object) => void,
 *   onDataChange?: (snapshot: { aggregator_error?: string|null, manual_review_count: number, is_empty: boolean, loaded: boolean }) => void,
 * }} props
 */
export function QuoteTaxPreflight({ projectId, open, overrides, onOverridesChange, onDataChange }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await getQuoteTaxPreflight(projectId);
      setData(res.data);
      if (onDataChange) {
        onDataChange({
          aggregator_error: res.data?.aggregator_error || null,
          manual_review_count: (res.data?.warnings?.manual_review_lines || []).length,
          is_empty: !!res.data?.is_empty,
          loaded: true,
        });
      }
    } catch (err) {
      setError(extractErrorMessage(err));
      setData(null);
      if (onDataChange) {
        onDataChange({
          aggregator_error: extractErrorMessage(err),
          manual_review_count: 0,
          is_empty: false,
          loaded: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  if (!open) return null;

  const client = data?.client || {};
  const warnings = data?.warnings || {};
  const hasManualReview = (warnings.manual_review_lines || []).length > 0;
  const hasMissing = (warnings.missing_tax_class || []).length > 0;
  const hasUnmatched = (warnings.unmatched_rule || []).length > 0;

  return (
    <div
      className="border border-white/10 rounded-md bg-slate-950/40 p-3 space-y-3"
      data-testid="quote-tax-preflight"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <ShieldCheck className="h-4 w-4 text-emerald-500" />
          Tax Policy Review
          <HelpPopover
            testId="quote-generation-preflight-help"
            title="What the Tax Policy Review shows"
          >
            <p>
              Before you generate this quote, this section previews the tax
              decisions that the Tax Rules Matrix + client tax treatment will
              produce for every line.
            </p>
            <p>
              <strong>Manual Review</strong> and <strong>Missing</strong>{" "}
              lines should be checked before issuing.
            </p>
            <p>
              For a <strong>UK VAT-registered business</strong> such as Opus,
              use billing country <code>GB</code>, entity{" "}
              <code>business</code>, VAT status <code>valid</code>, and the
              configured <code>non_eu_b2b_services</code> treatment.
            </p>
          </HelpPopover>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
          data-testid="quote-tax-preflight-refresh"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCcw className="h-3 w-3" />}
          Refresh
        </button>
      </div>

      {loading && !data && (
        <div className="text-xs text-muted-foreground">Analysing tax impact…</div>
      )}
      {error && !loading && (
        <Alert variant="destructive" className="py-2">
          <AlertDescription className="text-xs">
            Preflight failed: {error}
          </AlertDescription>
        </Alert>
      )}

      {data && data.aggregator_error && (
        <Alert className="py-2 border-amber-500/40 bg-amber-500/10" data-testid="quote-tax-preflight-aggregator-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle className="text-xs">Line aggregation unavailable</AlertTitle>
          <AlertDescription className="text-[11px] leading-snug">
            Estimated totals could not be computed — client tax profile is still
            shown below. ({data.aggregator_error})
          </AlertDescription>
        </Alert>
      )}

      {data && data.is_empty && !data.aggregator_error && (
        <Alert className="py-2 border-slate-500/40 bg-slate-500/10" data-testid="quote-tax-preflight-empty">
          <AlertDescription className="text-[11px] leading-snug">
            No shift line items found for this project — the generated quote
            will be empty until shifts / tasks are added.
          </AlertDescription>
        </Alert>
      )}

      {data && (
        <>
          {/* Client tax profile summary */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <div className="text-muted-foreground">Client</div>
            <div className="font-mono">{client.name || "—"}</div>
            <div className="text-muted-foreground">Billing country</div>
            <div className="font-mono">{client.billing_country || "—"}</div>
            <div className="text-muted-foreground">VAT / Tax number</div>
            <div className="font-mono">{client.vat_code || "—"}</div>
            <div className="text-muted-foreground">VAT number status</div>
            <div className="font-mono">{client.vat_number_status || "not_checked"}</div>
            <div className="text-muted-foreground">Client tax treatment</div>
            <div className="font-mono">{client.client_tax_treatment || "(unset — defaults to standard TVA)"}</div>
          </div>

          {/* Detected tax policy */}
          <div className="text-xs leading-relaxed p-2 rounded bg-slate-900/60 border border-white/5">
            <span className="font-semibold text-foreground">Detected policy: </span>
            <span className="text-muted-foreground">{data.detected_tax_policy}</span>
          </div>

          {/* Warnings */}
          {(hasManualReview || hasMissing || hasUnmatched) && (
            <Alert className="border-amber-500/40 bg-amber-500/10 py-2">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle className="text-xs">Attention required</AlertTitle>
              <AlertDescription className="text-[11px] leading-snug space-y-0.5">
                {hasManualReview && (
                  <div>
                    <strong>Manual review ({warnings.manual_review_lines.length}):</strong>{" "}
                    {warnings.manual_review_lines.slice(0, 3).join(" · ")}
                    {warnings.manual_review_lines.length > 3 ? ` … +${warnings.manual_review_lines.length - 3} more` : ""}
                  </div>
                )}
                {hasMissing && (
                  <div>
                    <strong>Missing tax_class ({warnings.missing_tax_class.length}):</strong>{" "}
                    {warnings.missing_tax_class.slice(0, 3).join(" · ")}
                    {warnings.missing_tax_class.length > 3 ? ` … +${warnings.missing_tax_class.length - 3} more` : ""}
                  </div>
                )}
                {hasUnmatched && (
                  <div>
                    <strong>No matching rule ({warnings.unmatched_rule.length}):</strong>{" "}
                    {warnings.unmatched_rule.slice(0, 3).join(" · ")}
                    {warnings.unmatched_rule.length > 3 ? ` … +${warnings.unmatched_rule.length - 3} more` : ""}
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Per-class table */}
          <div className="border border-white/5 rounded overflow-hidden">
            <table className="w-full text-xs" data-testid="quote-tax-preflight-breakdown">
              <thead className="bg-slate-900/60 text-muted-foreground">
                <tr>
                  <th className="text-left px-2 py-1">Line tax class</th>
                  <th className="text-right px-2 py-1">Lines</th>
                  <th className="text-right px-2 py-1">Net subtotal</th>
                  <th className="text-right px-2 py-1">Est. VAT</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.by_tax_class || {}).map(([k, v]) => (
                  <tr key={k} className="border-t border-white/5">
                    <td className="px-2 py-1 font-mono">
                      {k === "(missing)" ? (
                        <span className="text-amber-500">(missing)</span>
                      ) : k}
                    </td>
                    <td className="px-2 py-1 text-right">{v.count}</td>
                    <td className="px-2 py-1 text-right font-mono">{fmt(v.subtotal)}</td>
                    <td className="px-2 py-1 text-right font-mono">{fmt(v.vat_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Estimated totals */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <div className="text-muted-foreground">Reverse-charge subtotal</div>
            <div className="font-mono text-right">{fmt(data.reverse_charge_subtotal)}</div>
            <div className="text-muted-foreground">Taxable subtotal</div>
            <div className="font-mono text-right">{fmt(data.taxable_subtotal)}</div>
            <div className="text-muted-foreground">Estimated VAT</div>
            <div className="font-mono text-right">{fmt(data.estimated_vat_amount)}</div>
            <div className="font-semibold">Estimated total</div>
            <div className="font-mono text-right font-semibold">{fmt(data.estimated_total)}</div>
          </div>

          {/* Admin controls (display + reason) */}
          <div className="border-t border-white/5 pt-2 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className="font-medium">Apply Tax Rules Matrix</span>
                <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-500">
                  Always ON
                </Badge>
              </div>
              <Switch checked={true} disabled data-testid="preflight-apply-matrix" />
            </div>

            <div className="flex items-center justify-between text-xs">
              <span className="font-medium">Include reverse-charge note in PDF</span>
              <Switch
                checked={overrides.includeRcNote}
                onCheckedChange={(v) => onOverridesChange({ includeRcNote: v })}
                data-testid="preflight-include-rc-note"
              />
            </div>

            <div className="flex items-center justify-between text-xs">
              <span className="font-medium">
                Block issuing if Manual Review lines exist
              </span>
              <Switch
                checked={overrides.blockOnManualReview}
                onCheckedChange={(v) => onOverridesChange({ blockOnManualReview: v })}
                data-testid="preflight-block-on-manual-review"
              />
            </div>

            {(!overrides.includeRcNote || !overrides.blockOnManualReview) && (
              <div className="space-y-1">
                <Label className="text-xs text-amber-500">
                  Override reason (required when relaxing safety controls)
                </Label>
                <Textarea
                  value={overrides.overrideReason}
                  onChange={(e) => onOverridesChange({ overrideReason: e.target.value })}
                  placeholder="Explain why the safety default was relaxed…"
                  rows={2}
                  className="text-xs"
                  data-testid="preflight-override-reason"
                />
              </div>
            )}

            {hasManualReview && overrides.blockOnManualReview && (
              <Alert className="py-2 border-amber-500/40 bg-amber-500/10">
                <AlertDescription className="text-[11px]">
                  This quote may be generated as <strong>draft</strong>, but
                  issuing / sending will remain blocked until the manual-review
                  lines are checked.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default QuoteTaxPreflight;
